/* Rules a tenant writes for themselves.
 *
 * "A purchase order over a lakh needs my approval" is one sentence, and in
 * ERPNext it is four linked child tables. So the form here is the sentence:
 * the steps it goes through, who may move it on, and when. What comes out
 * drives the buttons on the real form for everybody, immediately.
 */
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useErp } from '@jackdesk/erp-kit'
import type { AutomationRules, ErpError } from '@jackdesk/erp-kit'
import { Failed, Loading, useToast } from '@jackdesk/shell'
import index from './doctypes.generated.json'
import { labelFor } from './nav'

export function Automation() {
  const { doctype = '' } = useParams()
  const navigate = useNavigate()
  const dt = decodeURIComponent(doctype)
  if (!dt) return <PickAForm onOpen={(d) => navigate(`/automation/${encodeURIComponent(d)}`)} />
  return <RulesFor doctype={dt} onBack={() => navigate('/automation')} />
}

function PickAForm({ onOpen }: { onOpen: (d: string) => void }) {
  const [text, setText] = useState('')
  const needle = text.trim().toLowerCase()
  const found = index
    .filter((d) => !d.admin && !d.g && d.s)          // submittable: where approvals live
    .map((d) => ({ name: d.n, label: labelFor(d.n), module: d.m }))
    .filter((d) => !needle || d.label.toLowerCase().includes(needle))
    .slice(0, 48)

  return (
    <div className="page" style={{ display: 'grid', gap: 16 }}>
      <div>
        <h1>Approvals and alerts</h1>
        <p className="muted small" style={{ marginTop: 4, maxWidth: '62ch' }}>
          Decide what has to be approved before it goes out, who approves it, and what
          you want to be told about. A rule you make here changes the buttons on the
          form for everybody, straight away.
        </p>
      </div>
      <input className="input" style={{ maxWidth: 320 }} value={text}
             placeholder="Which form?" aria-label="Which form"
             onChange={(e) => setText(e.target.value)} />
      <div style={{ display: 'grid', gap: 6, gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }}>
        {found.map((d) => (
          <button key={d.name} className="card card-pad" style={{ textAlign: 'left', cursor: 'pointer' }}
                  onClick={() => onOpen(d.name)}>
            <div style={{ fontWeight: 560 }}>{d.label}</div>
            <div className="small muted">{d.module}</div>
          </button>
        ))}
      </div>
    </div>
  )
}

function RulesFor({ doctype, onBack }: { doctype: string; onBack: () => void }) {
  const erp = useErp()
  const toast = useToast()
  const [data, setData] = useState<AutomationRules | null>(null)
  const [failed, setFailed] = useState<ErpError | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(() => {
    setFailed(null)
    erp.automationFor(doctype).then(setData).catch((e) => setFailed(e as ErpError))
  }, [erp, doctype])
  useEffect(load, [load])

  const [title, setTitle] = useState(`${labelFor(doctype)} needs approving`)
  const [waiting, setWaiting] = useState('Waiting for approval')
  const [approved, setApproved] = useState('Approved')
  const [approver, setApprover] = useState('')
  const [onlyIf, setOnlyIf] = useState('')

  if (failed) {
    return <div className="page">
      <Failed message={failed.message} requestId={failed.requestId} onRetry={load} />
    </div>
  }
  if (!data) return <div className="page"><Loading rows={5} label="Reading the rules" /></div>

  const build = async () => {
    setBusy(true)
    try {
      await erp.buildWorkflow(doctype, {
        name: title, field: 'workflow_state',
        steps: [
          { state: waiting, who: 'All', doc_status: 0 },
          { state: approved, who: approver || 'All', doc_status: 0 },
        ],
        moves: [{
          from: waiting, action: 'Approve', to: approved,
          who: approver || 'All', ...(onlyIf.trim() ? { only_if: onlyIf.trim() } : {}),
        }],
      })
      toast('ok', 'Done. The form has an Approve button on it now.')
      load()
    } catch (e) { toast('error', (e as ErpError).message) }
    finally { setBusy(false) }
  }

  return (
    <div className="page" style={{ display: 'grid', gap: 16, maxWidth: 900 }}>
      <div>
        <button className="linkish small" onClick={onBack}>← All forms</button>
        <h1 style={{ marginTop: 4 }}>{labelFor(doctype)}</h1>
      </div>

      <section className="card card-pad stack" style={{ gap: 10 }} data-part="build">
        <strong className="small">Make one of these need approving</strong>
        <div className="row" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label className="field" style={{ minWidth: 220 }}>
            <span style={{ fontSize: 12.5, fontWeight: 550, color: 'var(--ink-2)' }}>Call it</span>
            <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} />
          </label>
          <label className="field" style={{ minWidth: 175 }}>
            <span style={{ fontSize: 12.5, fontWeight: 550, color: 'var(--ink-2)' }}>
              Starts as
            </span>
            <input className="input" value={waiting} onChange={(e) => setWaiting(e.target.value)} />
          </label>
          <label className="field" style={{ minWidth: 175 }}>
            <span style={{ fontSize: 12.5, fontWeight: 550, color: 'var(--ink-2)' }}>
              Once approved
            </span>
            <input className="input" value={approved} onChange={(e) => setApproved(e.target.value)} />
          </label>
          <label className="field" style={{ minWidth: 190 }}>
            <span style={{ fontSize: 12.5, fontWeight: 550, color: 'var(--ink-2)' }}>
              Who approves it
            </span>
            <input className="input" value={approver} placeholder="Anybody"
                   onChange={(e) => setApprover(e.target.value)} />
          </label>
        </div>
        <label className="field">
          <span style={{ fontSize: 12.5, fontWeight: 550, color: 'var(--ink-2)' }}>
            Only when… (leave empty for always)
          </span>
          <input className="input" value={onlyIf} placeholder="doc.grand_total &gt; 100000"
                 onChange={(e) => setOnlyIf(e.target.value)} />
        </label>
        <div className="row">
          <span className="small muted">
            Everyone sees the change on their next load. Nothing already saved moves.
          </span>
          <span className="grow" />
          <button className="btn btn-primary" disabled={busy || !title.trim()} onClick={() => void build()}>
            {busy ? 'Setting up…' : 'Set it up'}
          </button>
        </div>
      </section>

      {data.workflows.length ? (
        <section className="card">
          <p className="small muted" style={{ padding: '10px 12px 0', margin: 0 }}>
            What is set up. Turning one off leaves documents where they are.
          </p>
          <table className="tbl">
            <thead><tr><th>Approval</th><th>Steps</th><th></th><th></th></tr></thead>
            <tbody>
              {data.workflows.map((w) => (
                <tr key={w.name} style={{ cursor: 'default' }}>
                  <td>{w.workflow_name}</td>
                  <td className="small muted">
                    {w.states.map((s) => s.state).join(' → ')}
                    {w.transitions[0]?.condition ? ` · only when ${w.transitions[0].condition}` : ''}
                  </td>
                  <td>{w.is_active
                    ? <span className="chip chip-ok">on</span>
                    : <span className="chip">off</span>}</td>
                  <td>
                    <div className="row" style={{ gap: 6 }}>
                      <button className="btn btn-sm" onClick={async () => {
                        await erp.switchWorkflow(doctype, w.name, !w.is_active)
                        toast('ok', w.is_active ? 'Turned off.' : 'Turned on.'); load()
                      }}>{w.is_active ? 'Turn off' : 'Turn on'}</button>
                      <button className="btn btn-sm" onClick={async () => {
                        await erp.removeWorkflow(doctype, w.name)
                        toast('ok', 'Removed.'); load()
                      }}>Remove</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}

      <Others data={data} />
    </div>
  )
}

/* Alerts, who-picks-it-up and webhooks are ordinary forms the engine already
 * draws, so they are listed and linked rather than rebuilt here. */
function Others({ data }: { data: AutomationRules }) {
  const navigate = useNavigate()
  const groups: [string, string, { name: string }[], string][] = [
    ['Notification', 'Being told about things', data.notifications, 'when something happens'],
    ['Assignment Rule', 'Who picks it up', data.assignment_rules, 'send new ones to somebody'],
    ['Webhook', 'Telling another system', data.webhooks, 'push it somewhere else'],
  ]
  return (
    <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}>
      {groups.map(([dt, title, rows, hint]) => (
        <div key={dt} className="card card-pad">
          <strong className="small">{title}</strong>
          <p className="small muted" style={{ margin: '2px 0 8px' }}>
            {rows.length ? `${rows.length} set up` : `Nothing yet — ${hint}.`}
          </p>
          <button className="btn btn-sm" onClick={() => navigate(`/list/${encodeURIComponent(dt)}`)}>
            {rows.length ? 'See them' : 'Set one up'}
          </button>
        </div>
      ))}
    </div>
  )
}
